import { Button, cn, EmptyState, Skeleton } from '@repo/ui'
import {
  createFileRoute,
  Link,
  useLocation,
  useParams,
} from '@tanstack/react-router'
import {
  ArrowLeft,
  BookOpen,
  CalendarClock,
  Check,
  FlaskConical,
  Settings as SettingsIcon,
  ShieldCheck,
  Sparkles,
  User as UserIcon,
  Wand2,
  Workflow,
  Wrench,
} from 'lucide-react'
import type { ElementType } from 'react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { FollowUpTab } from '@/features/agents-v2/followup-tab'
import { KnowledgeTab } from '@/features/agents-v2/knowledge-tab'
import { McpTab } from '@/features/agents-v2/mcp-tab'
import { OptimizerTab } from '@/features/agents-v2/optimizer-tab'
import { ProfileTab } from '@/features/agents-v2/profile-tab'
import {
  createDefaultOperationalPolicy,
  QualificationTab,
} from '@/features/agents-v2/qualification-tab'
import { SchedulingTab } from '@/features/agents-v2/scheduling-tab'
import { SecurityTab } from '@/features/agents-v2/security-tab'
import { SettingsTab } from '@/features/agents-v2/settings-tab'
import { TestPanel } from '@/features/agents-v2/test-panel'
import { TestsTab } from '@/features/agents-v2/tests-tab'
import { useAgentDetail, useUpdateAgent } from '@/hooks/use-agents'
import { useAuth } from '@/hooks/use-auth'
import { useChannels } from '@/hooks/use-channels'
import { useInboxMembers } from '@/hooks/use-inbox-members'
import { useKnowledgeBases } from '@/hooks/use-knowledge'
import { useCustomFields, useKanbanStages, useTags } from '@/hooks/use-leads'
import {
  useMcpAgentBindings,
  useMcpConnections,
  useMcpToolsByConnections,
  useUpsertMcpAgentBinding,
} from '@/hooks/use-mcp'
import {
  useSchedulingProfiles,
  useUpdateSchedulingProfile,
} from '@/hooks/use-scheduling-profiles'
import { useDepartments } from '@/hooks/use-settings'
import type {
  AgentInput,
  AgentOperationalPolicy,
  AgentPolicyMode,
  AgentSegmentation,
  AgentTone,
  SchedulingAssignmentStrategy,
} from '@/types/agents'
import type { SchedulingProfile } from '@/types/appointments'

export const Route = createFileRoute('/_app/agentes/$agentId')({
  component: Agent2DetailPage,
})

type TabKey =
  | 'profile'
  | 'knowledge'
  | 'settings'
  | 'qualification'
  | 'scheduling'
  | 'automations'
  | 'security'
  | 'mcp'
  | 'optimizer'
  | 'tests'

const TABS: {
  key: TabKey
  label: string
  icon: ElementType
  to: string
}[] = [
  { key: 'profile', label: 'Perfil', icon: UserIcon, to: '/agentes/$agentId' },
  {
    key: 'knowledge',
    label: 'Conhecimento',
    icon: BookOpen,
    to: '/agentes/$agentId/conhecimento',
  },
  {
    key: 'qualification',
    label: 'Qualificação',
    icon: Sparkles,
    to: '/agentes/$agentId/qualificacao',
  },
  {
    key: 'scheduling',
    label: 'Agendamento',
    icon: CalendarClock,
    to: '/agentes/$agentId/agendamento',
  },
  {
    key: 'automations',
    label: 'Automações',
    icon: Workflow,
    to: '/agentes/$agentId/automacoes',
  },
  {
    key: 'security',
    label: 'Segurança',
    icon: ShieldCheck,
    to: '/agentes/$agentId/seguranca',
  },
  { key: 'mcp', label: 'MCP', icon: Wrench, to: '/agentes/$agentId/mcp' },
  {
    key: 'settings',
    label: 'Configurações',
    icon: SettingsIcon,
    to: '/agentes/$agentId/configuracoes',
  },
  {
    key: 'optimizer',
    label: 'Otimizador',
    icon: Wand2,
    to: '/agentes/$agentId/otimizador',
  },
  {
    key: 'tests',
    label: 'Testes',
    icon: FlaskConical,
    to: '/agentes/$agentId/testes',
  },
]

const DEFAULT_SEG: AgentSegmentation = {
  max_chars: 280,
  min_chars: 80,
  delay_ms_per_char: 35,
}

const EMPTY_SCHEDULING_PROFILES: SchedulingProfile[] = []

export function Agent2DetailPage({
  activeTab: forcedTab,
}: {
  activeTab?: TabKey
}) {
  const { agentId } = useParams({ strict: false }) as { agentId: string }
  const location = useLocation()
  const activeTab = forcedTab ?? tabFromPathname(location.pathname)
  const { currentOrgId } = useAuth()

  const { data: agent, isLoading } = useAgentDetail(currentOrgId, agentId)
  const { data: knowledgeResponse, isLoading: knowledgeLoading } =
    useKnowledgeBases(currentOrgId, activeTab === 'knowledge')
  const { data: customFields = [] } = useCustomFields(
    currentOrgId,
    activeTab === 'qualification'
  )
  const { data: kanbanStages = [] } = useKanbanStages(currentOrgId)
  const { data: tags = [] } = useTags(currentOrgId)
  const updateAgent = useUpdateAgent(currentOrgId, agentId)
  const { data: channels = [] } = useChannels(
    currentOrgId,
    activeTab === 'settings' || activeTab === 'optimizer'
  )
  const { data: departments = [] } = useDepartments(
    currentOrgId,
    activeTab === 'settings'
  )
  const { data: members = [] } = useInboxMembers(currentOrgId)
  const { data: schedulingProfilesData } = useSchedulingProfiles(
    currentOrgId,
    activeTab === 'scheduling'
  )
  const { data: mcpConnections = [], isLoading: mcpConnectionsLoading } =
    useMcpConnections(currentOrgId, activeTab === 'mcp')
  const mcpTools = useMcpToolsByConnections(
    currentOrgId,
    mcpConnections,
    activeTab === 'mcp'
  )
  const { data: mcpBindings = [], isLoading: mcpBindingsLoading } =
    useMcpAgentBindings(currentOrgId, agentId, activeTab === 'mcp')
  const upsertMcpBinding = useUpsertMcpAgentBinding(currentOrgId, agentId)
  const schedulingProfiles = schedulingProfilesData ?? EMPTY_SCHEDULING_PROFILES
  const updateSchedulingProfile = useUpdateSchedulingProfile(currentOrgId)

  // Agent field state
  const [name, setName] = useState('')
  const [assistantName, setAssistantName] = useState('')
  const [description, setDescription] = useState('')
  const [instructions, setInstructions] = useState('')
  const [personality, setPersonality] = useState('')
  const [welcomeMessage, setWelcomeMessage] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [companyDescription, setCompanyDescription] = useState('')
  const [model, setModel] = useState('gpt-4o-mini')
  const [tone, setTone] = useState<AgentTone>('FRIENDLY')
  const [temperature, setTemperature] = useState(0.7)
  const [isActive, setIsActive] = useState(true)
  const [isDefault, setIsDefault] = useState(false)
  const [debounceSeconds, setDebounceSeconds] = useState(8)
  const [historyLimit, setHistoryLimit] = useState(20)
  const [maxTokens, setMaxTokens] = useState(1024)
  const [waitForCadence, setWaitForCadence] = useState(false)
  const [segmentation, setSegmentation] =
    useState<AgentSegmentation>(DEFAULT_SEG)
  const [linkedKbIds, setLinkedKbIds] = useState<string[]>([])
  const [linkedProfileIds, setLinkedProfileIds] = useState<string[]>([])
  const [qualificationRequiredFields, setQualificationRequiredFields] =
    useState<string[]>([])
  const [policyMode, setPolicyMode] = useState<AgentPolicyMode>('LEGACY')
  const [operationalPolicy, setOperationalPolicy] =
    useState<AgentOperationalPolicy>(() => createDefaultOperationalPolicy())
  const [allowedTagIds, setAllowedTagIds] = useState<string[]>([])
  const [transferOnRequest, setTransferOnRequest] = useState(true)
  const [transferOnConfusion, setTransferOnConfusion] = useState(true)
  const [transferOnHotLead, setTransferOnHotLead] = useState(false)
  const [schedulingAssignmentStrategy, setSchedulingAssignmentStrategy] =
    useState<SchedulingAssignmentStrategy>('LLM')

  // Auto-save refs
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Garante que o state local seja semeado uma única vez por agente — refetches
  // do mesmo agente (ex.: após auto-save) não devem sobrescrever o que o usuário
  // está editando, senão o cursor salta para o final.
  const hydratedAgentIdRef = useRef<string | null>(null)
  const stateRef = useRef({
    name,
    assistantName,
    description,
    instructions,
    personality,
    welcomeMessage,
    companyName,
    companyDescription,
    model,
    tone,
    temperature,
    isActive,
    isDefault,
    debounceSeconds,
    historyLimit,
    maxTokens,
    waitForCadence,
    segmentation,
    linkedKbIds,
    qualificationRequiredFields,
    policyMode,
    operationalPolicy,
    allowedTagIds,
    transferOnRequest,
    transferOnConfusion,
    transferOnHotLead,
    schedulingAssignmentStrategy,
  })

  // Sync stateRef on every state change
  useEffect(() => {
    stateRef.current = {
      name,
      assistantName,
      description,
      instructions,
      personality,
      welcomeMessage,
      companyName,
      companyDescription,
      model,
      tone,
      temperature,
      isActive,
      isDefault,
      debounceSeconds,
      historyLimit,
      maxTokens,
      waitForCadence,
      segmentation,
      linkedKbIds,
      qualificationRequiredFields,
      policyMode,
      operationalPolicy,
      allowedTagIds,
      transferOnRequest,
      transferOnConfusion,
      transferOnHotLead,
      schedulingAssignmentStrategy,
    }
  }, [
    name,
    assistantName,
    description,
    instructions,
    personality,
    welcomeMessage,
    companyName,
    companyDescription,
    model,
    tone,
    temperature,
    isActive,
    isDefault,
    debounceSeconds,
    historyLimit,
    maxTokens,
    waitForCadence,
    segmentation,
    linkedKbIds,
    qualificationRequiredFields,
    policyMode,
    operationalPolicy,
    allowedTagIds,
    transferOnRequest,
    transferOnConfusion,
    transferOnHotLead,
    schedulingAssignmentStrategy,
  ])

  // Initialize state from fetched agent
  useEffect(() => {
    if (!agent) return
    if (hydratedAgentIdRef.current === agentId) return
    hydratedAgentIdRef.current = agentId
    setName(agent.name)
    setAssistantName(agent.assistantName ?? '')
    setDescription(agent.description ?? '')
    setInstructions(agent.instructions ?? '')
    setPersonality(agent.personality ?? '')
    setWelcomeMessage(agent.welcomeMessage ?? '')
    setCompanyName(agent.companyName ?? '')
    setCompanyDescription(agent.companyDescription ?? '')
    setModel(agent.model || 'gpt-4o-mini')
    setTone(agent.tone ?? 'FRIENDLY')
    setTemperature(Number(agent.temperature ?? 0.7))
    setIsActive(agent.isActive)
    setIsDefault(agent.isDefault)
    setDebounceSeconds(agent.debounce_seconds ?? 8)
    setHistoryLimit(agent.historyLimit ?? 20)
    setMaxTokens(agent.maxTokens ?? 1024)
    setWaitForCadence(agent.waitForCadence)
    setSegmentation(agent.segmentation ?? DEFAULT_SEG)
    setLinkedKbIds(
      agent.knowledgeBaseIds ??
        (agent.knowledgeBaseId ? [agent.knowledgeBaseId] : [])
    )
    setQualificationRequiredFields(agent.qualificationRequiredFields ?? [])
    setPolicyMode(agent.policyMode ?? 'LEGACY')
    setOperationalPolicy(
      agent.operationalPolicy ??
        createDefaultOperationalPolicy(agent.qualificationRequiredFields ?? [])
    )
    setAllowedTagIds(agent.allowedTagIds ?? [])
    setTransferOnRequest(agent.transferOnRequest ?? true)
    setTransferOnConfusion(agent.transferOnConfusion ?? true)
    setTransferOnHotLead(agent.transferOnHotLead ?? false)
    setSchedulingAssignmentStrategy(agent.schedulingAssignmentStrategy ?? 'LLM')
  }, [agent, agentId])

  // Initialize scheduling profile
  useEffect(() => {
    if (!schedulingProfilesData) return

    const nextIds = schedulingProfilesData
      .filter((profile) => profile.agentId === agentId)
      .map((profile) => profile.id)

    setLinkedProfileIds((currentIds) => {
      if (
        currentIds.length === nextIds.length &&
        currentIds.every((id, index) => id === nextIds[index])
      ) {
        return currentIds
      }
      return nextIds
    })
  }, [schedulingProfilesData, agentId])

  function buildPayload(s = stateRef.current): AgentInput {
    return {
      name: s.name.trim(),
      assistantName: s.assistantName.trim() || null,
      description: s.description.trim() || null,
      instructions: s.instructions,
      personality: s.personality.trim() || null,
      welcomeMessage: s.welcomeMessage.trim() || null,
      companyName: s.companyName.trim() || null,
      companyDescription: s.companyDescription.trim() || null,
      model: s.model,
      tone: s.tone,
      temperature: s.temperature,
      isActive: s.isActive,
      isDefault: s.isDefault,
      historyLimit: s.historyLimit,
      maxTokens: s.maxTokens,
      waitForCadence: s.waitForCadence,
      segmentation: s.segmentation,
      knowledgeBaseIds: s.linkedKbIds,
      qualificationRequiredFields: s.qualificationRequiredFields,
      policyMode: s.policyMode,
      operationalPolicy: s.operationalPolicy,
      allowedTagIds: s.allowedTagIds,
      transferOnRequest: s.transferOnRequest,
      transferOnConfusion: s.transferOnConfusion,
      transferOnHotLead: s.transferOnHotLead,
      schedulingAssignmentStrategy:
        s.schedulingAssignmentStrategy as AgentInput['schedulingAssignmentStrategy'],
    }
  }

  function scheduleSave() {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(async () => {
      const s = stateRef.current
      if (!s.name.trim()) return
      try {
        await updateAgent.mutateAsync(buildPayload(s))
      } catch {
        toast.error('Erro ao salvar agente')
      }
    }, 800)
  }

  function handleProfileChange(key: string, value: string) {
    switch (key) {
      case 'name':
        setName(value)
        break
      case 'assistantName':
        setAssistantName(value)
        break
      case 'description':
        setDescription(value)
        break
      case 'instructions':
        setInstructions(value)
        break
      case 'personality':
        setPersonality(value)
        break
      case 'welcomeMessage':
        setWelcomeMessage(value)
        break
      case 'companyName':
        setCompanyName(value)
        break
      case 'companyDescription':
        setCompanyDescription(value)
        break
    }
    scheduleSave()
  }

  function handleSettingsChange(key: string, value: unknown) {
    switch (key) {
      case 'tone':
        setTone(value as AgentTone)
        break
      case 'isActive':
        setIsActive(value as boolean)
        break
      case 'isDefault':
        setIsDefault(value as boolean)
        break
      case 'waitForCadence':
        setWaitForCadence(value as boolean)
        break
    }
    scheduleSave()
  }

  function handleAttendanceChange(key: string, value: boolean | string) {
    switch (key) {
      case 'transferOnRequest':
        setTransferOnRequest(value as boolean)
        break
      case 'transferOnConfusion':
        setTransferOnConfusion(value as boolean)
        break
      case 'transferOnHotLead':
        setTransferOnHotLead(value as boolean)
        break
      case 'schedulingAssignmentStrategy':
        setSchedulingAssignmentStrategy(value as SchedulingAssignmentStrategy)
        break
    }
    scheduleSave()
  }

  async function handleSchedulingProfileToggle(
    profileId: string,
    checked: boolean
  ) {
    const previousIds = linkedProfileIds
    const nextIds = checked
      ? [...new Set([...previousIds, profileId])]
      : previousIds.filter((id) => id !== profileId)

    setLinkedProfileIds(nextIds)
    try {
      await updateSchedulingProfile.mutateAsync({
        id: profileId,
        data: { agentId: checked ? agentId : null },
      })
    } catch {
      setLinkedProfileIds(previousIds)
      toast.error('Erro ao atualizar perfil de agendamento')
    }
  }

  async function handleMcpBindingChange(
    toolId: string,
    patch: { enabled?: boolean; confirmation?: 'NONE' | 'HUMAN_APPROVAL' }
  ) {
    try {
      await upsertMcpBinding.mutateAsync({ toolId, ...patch })
    } catch {
      toast.error('Erro ao atualizar ferramenta MCP do agente')
    }
  }

  async function handlePublish() {
    setIsActive(true)
    try {
      await updateAgent.mutateAsync({ ...buildPayload(), isActive: true })
      toast.success('Agente publicado')
    } catch {
      toast.error('Erro ao publicar agente')
    }
  }

  if (!currentOrgId) {
    return (
      <div className="text-muted-foreground flex flex-1 items-center justify-center">
        Selecione uma organização
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex min-h-0 flex-1 flex-col p-6">
        <Skeleton className="h-8 w-72" />
        <Skeleton className="mt-6 h-20 w-full" />
        <Skeleton className="mt-4 h-96 w-full" />
      </div>
    )
  }

  if (!agent) {
    return (
      <EmptyState
        variant="inline"
        className="min-h-80"
        icon={Sparkles}
        title="Agente não encontrado"
        description="O agente pode ter sido removido ou você não tem acesso."
        action={
          <Button asChild variant="outline">
            <Link to="/agentes">
              <ArrowLeft className="h-4 w-4" />
              Voltar para Agentes IA
            </Link>
          </Button>
        }
      />
    )
  }

  const knowledgeBases = knowledgeResponse?.data ?? []

  return (
    <div className="flex h-[calc(100svh-3.5rem)] min-h-0 overflow-hidden">
      {/* Left nav column */}
      <nav className="border-border bg-card flex w-52 shrink-0 flex-col gap-0.5 overflow-y-auto border-r px-2 py-3">
        {/* Agent header in nav */}
        <div className="mb-1 px-3 py-3">
          <div className="flex min-w-0 items-center gap-2.5">
            <div className="bg-primary/10 flex size-8 shrink-0 items-center justify-center rounded-lg">
              <Sparkles className="text-primary size-4" />
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold">
                {name || 'Sem nome'}
              </p>
              <div className="mt-0.5 flex items-center gap-1">
                <span
                  className={cn(
                    'inline-flex h-1.5 w-1.5 shrink-0 rounded-full',
                    isActive ? 'bg-success/100' : 'bg-muted-foreground'
                  )}
                />
                <span className="text-muted-foreground text-xs">
                  {isActive ? 'Ativo' : 'Inativo'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Nav items */}
        {TABS.map((t) => (
          <Link
            key={t.key}
            to={t.to}
            params={{ agentId }}
            className={cn(
              'flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm transition-colors',
              activeTab === t.key
                ? 'bg-primary/10 text-primary font-medium'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            )}
          >
            <t.icon className="size-4 shrink-0" />
            {t.label}
          </Link>
        ))}

        {/* Spacer + publish button at bottom */}
        <div className="mt-auto px-1 pt-3">
          {updateAgent.isPending && (
            <p className="text-muted-foreground mb-2 text-center text-xs">
              Salvando...
            </p>
          )}
          {isActive ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled
              className="w-full"
            >
              <Check className="h-3.5 w-3.5" />
              Publicado
            </Button>
          ) : (
            <Button
              type="button"
              size="sm"
              className="w-full"
              onClick={() => void handlePublish()}
              disabled={updateAgent.isPending}
            >
              Publicar
            </Button>
          )}
        </div>
      </nav>

      {/* Center — scrollable content */}
      <div className="flex flex-1 flex-col overflow-y-auto">
        <div className="[&_[data-slot=input]]:bg-muted/20 [&_[data-slot=textarea]]:bg-muted/20 mx-auto w-full max-w-3xl px-6 py-6">
          {activeTab === 'profile' && (
            <ProfileTab
              orgId={currentOrgId}
              agentId={agentId}
              name={name}
              assistantName={assistantName}
              description={description}
              instructions={instructions}
              personality={personality}
              welcomeMessage={welcomeMessage}
              companyName={companyName}
              companyDescription={companyDescription}
              tone={tone}
              onChange={handleProfileChange}
            />
          )}
          {activeTab === 'knowledge' && (
            <KnowledgeTab
              knowledgeBases={knowledgeBases}
              linkedKbIds={linkedKbIds}
              isLoading={knowledgeLoading}
              onChange={(ids) => {
                setLinkedKbIds(ids)
                scheduleSave()
              }}
            />
          )}
          {activeTab === 'settings' && (
            <SettingsTab
              tone={tone}
              isActive={isActive}
              isDefault={isDefault}
              waitForCadence={waitForCadence}
              agentId={agentId}
              orgId={currentOrgId}
              members={members}
              channels={channels}
              departments={departments}
              onChange={handleSettingsChange}
            />
          )}
          {activeTab === 'qualification' && (
            <QualificationTab
              customFields={customFields}
              stages={kanbanStages}
              policy={operationalPolicy}
              onPolicyChange={(policy) => {
                setOperationalPolicy(policy)
                setQualificationRequiredFields(
                  policy.qualification.fields
                    .filter((field) => field.requirement === 'REQUIRED')
                    .map((field) => field.fieldId)
                )
                scheduleSave()
              }}
            />
          )}
          {activeTab === 'scheduling' && (
            <SchedulingTab
              linkedProfileIds={linkedProfileIds}
              schedulingProfiles={schedulingProfiles}
              agentId={agentId}
              strategy={schedulingAssignmentStrategy}
              onStrategyChange={(strategy) => {
                setSchedulingAssignmentStrategy(strategy)
                scheduleSave()
              }}
              onProfileToggle={(profileId, checked) =>
                void handleSchedulingProfileToggle(profileId, checked)
              }
              isUpdatingProfiles={updateSchedulingProfile.isPending}
            />
          )}
          {activeTab === 'automations' && (
            <FollowUpTab agentId={agentId} agentName={name} />
          )}
          {activeTab === 'security' && (
            <SecurityTab
              mode={policyMode}
              policy={operationalPolicy}
              tags={tags}
              allowedTagIds={allowedTagIds}
              transferOnRequest={transferOnRequest}
              transferOnConfusion={transferOnConfusion}
              transferOnHotLead={transferOnHotLead}
              onModeChange={(mode) => {
                setPolicyMode(mode)
                scheduleSave()
              }}
              onPolicyChange={(policy) => {
                setOperationalPolicy(policy)
                scheduleSave()
              }}
              onAllowedTagsChange={(ids) => {
                setAllowedTagIds(ids)
                scheduleSave()
              }}
              onTransferChange={(key, value) =>
                handleAttendanceChange(key, value)
              }
            />
          )}
          {activeTab === 'mcp' && (
            <McpTab
              connections={mcpConnections}
              toolsByConnection={mcpTools.byConnectionId}
              bindings={mcpBindings}
              isLoading={
                mcpConnectionsLoading ||
                mcpTools.isLoading ||
                mcpBindingsLoading
              }
              isUpdating={upsertMcpBinding.isPending}
              onBindingChange={(toolId, patch) =>
                void handleMcpBindingChange(toolId, patch)
              }
            />
          )}
          {activeTab === 'tests' && (
            <TestsTab orgId={currentOrgId ?? ''} agentId={agentId} />
          )}
          {activeTab === 'optimizer' && (
            <OptimizerTab
              agentId={agentId}
              channels={channels.map((c) => ({ id: c.id, name: c.name }))}
            />
          )}
        </div>
      </div>

      {/* Right — test panel */}
      <div className="flex w-[380px] shrink-0 flex-col overflow-hidden">
        <TestPanel
          agentId={agentId}
          agentName={name || 'Agente'}
          orgId={currentOrgId}
        />
      </div>
    </div>
  )
}

function tabFromPathname(pathname: string): TabKey {
  if (pathname.endsWith('/conhecimento')) return 'knowledge'
  if (pathname.endsWith('/qualificacao')) return 'qualification'
  if (pathname.endsWith('/agendamento')) return 'scheduling'
  if (pathname.endsWith('/automacoes')) return 'automations'
  if (pathname.endsWith('/seguranca')) return 'security'
  if (pathname.endsWith('/mcp')) return 'mcp'
  if (pathname.endsWith('/configuracoes')) return 'settings'
  if (pathname.endsWith('/otimizador')) return 'optimizer'
  if (pathname.endsWith('/testes')) return 'tests'
  return 'profile'
}
